import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import {
    Modal,
    Row,
    Table,
    InputNumber,
    Button,
    Space,
    TreeSelect,
    Switch,
    Upload,
    Progress,
    Tooltip,
    Spin,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import styles from 'components/settings/ExpenseGroups/SettingsModal/index.module.scss';
import 'components/settings/ExpenseGroups/SettingsModal/index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
    DeleteOutlined,
    UploadOutlined,
    DownloadOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';
import {
    AllocationCsvRow,
    isPercentageTotalValid,
    parseAllocationCsv,
} from 'utils/allocationCsv';
import {
    updateOneOffConfigurations,
    IOneOffConfigurations,
    IExpenseConfigAllocation,
} from 'redux/slice/oneOffSlice';
import { getApiCSV } from 'redux/apis';
import { toastText } from 'utils/utils';
import { BlueAddSvg } from 'utils/svgs';

interface Props {
    isModalOpen: boolean;
    handleCancel: () => void;
    onAllocationSaved?: () => void;
}

interface AllocationRow {
    id: string;
    customer?: string;
    class?: string;
    location?: string;
    percentage?: number;
}

const OneOffSettingsModal: FC<Props> = ({
    isModalOpen,
    handleCancel,
    onAllocationSaved,
}) => {
    const dispatch = useDispatch<AppDispatch>();

    // Redux option lists
    const { optionData: qbClass } = useSelector((state: any) => state.class);
    const { data: qbChartOfAccounts } = useSelector(
        (state: any) => state.chartOfAccounts
    );
    const { optionData: qbCustomers } = useSelector(
        (state: any) => state.customer
    );
    const { optionData: qbLocations } = useSelector(
        (state: any) => state.locations
    );

    const { oneOffConfigurations } = useSelector((state: any) => state.oneOff);

    const transformToTreeData = (data: any[]): any[] => {
        if (!Array.isArray(data)) {
            return [];
        }

        return (data || [])
            .filter((item: any) => item && item.Id)
            .map((item: any): any => ({
                title:
                    item.Name ||
                    item.title ||
                    item.DisplayName ||
                    item.FullyQualifiedName,
                value: item.Id,
                key: item.Id,
                disabled: item.disabled === true,
                children: item.children && Array.isArray(item.children) && item.children.length > 0
                    ? transformToTreeData(item.children)
                    : [],
            }));
    };

    const treeData = useMemo(
        () => ({
            accounts: transformToTreeData(qbChartOfAccounts || []),
            customers: transformToTreeData(qbCustomers || []),
            classes: transformToTreeData(qbClass || []),
            locations: transformToTreeData(qbLocations || []),
        }),
        [qbChartOfAccounts, qbCustomers, qbClass, qbLocations]
    );

    // Helpers to validate/match (recursive for nested children)
    const flattenTree = (
        nodes: any[]
    ): Array<{ title: string; value: string }> => {
        const out: Array<{ title: string; value: string }> = [];
        const flatten = (items: any[]) => {
            items?.forEach((item: any) => {
                out.push({ title: item.title, value: item.value });
                if (item.children && Array.isArray(item.children) && item.children.length > 0) {
                    flatten(item.children);
                }
            });
        };
        flatten(nodes);
        return out;
    };

    const customerIndex = useMemo(
        () => flattenTree(treeData.customers),
        [treeData.customers]
    );
    const classIndex = useMemo(
        () => flattenTree(treeData.classes),
        [treeData.classes]
    );
    const locationIndex = useMemo(
        () => flattenTree(treeData.locations),
        [treeData.locations]
    );

    // Helper function to get account name by ID
    const getAccountNameById = useCallback(
        (accountId: string): string | undefined => {
            if (!accountId || !treeData.accounts) return undefined;
            const flattenAccounts = flattenTree(treeData.accounts);
            const account = flattenAccounts.find((acc) => acc.value === accountId);
            return account?.title;
        },
        [treeData.accounts]
    );

    const findTitleByValue = (
        value?: string,
        list?: Array<{ title: string; value: string }>
    ) => {
        if (!value || !list) return undefined;
        const foundOption = list.find((option) => option.value === value);
        return foundOption?.title;
    };

    const generateUniqueId = useCallback(
        () => `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        []
    );

    const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
    const [singleAccountOnly, setSingleAccountOnly] = useState<boolean>(false);
    const [rows, setRows] = useState<AllocationRow[]>([
        { id: generateUniqueId(), percentage: 0 },
    ]);
    const [validationError, setValidationError] = useState<string>('');
    const [csvErrors, setCsvErrors] = useState<string[]>([]);
    const [sampleLoading, setSampleLoading] = useState<boolean>(false);
    const [proceedLoading, setProceedLoading] = useState<boolean>(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [importLoading, setImportLoading] = useState(false);

    // Initialize from Redux when modal opens
    useEffect(() => {
        if (isModalOpen) {
            setCsvErrors([]);
            if (oneOffConfigurations) {
                setSingleAccountOnly(oneOffConfigurations.isSingleAccountAllocation ?? false);

                // If single account mode is ON, show configAccount in selectedAccounts
                // If single account mode is OFF, clear selectedAccounts (accounts come from transaction tab filters)
                if (oneOffConfigurations.isSingleAccountAllocation && oneOffConfigurations.configAccount) {
                    setSelectedAccounts([oneOffConfigurations.configAccount.value]);
                } else {
                    setSelectedAccounts([]);
                }

                const mapped: AllocationRow[] = (
                    oneOffConfigurations.expenseConfigAllocations || []
                ).map((item: IExpenseConfigAllocation) => ({
                    id: generateUniqueId(),
                    customer: item.customerId,
                    class: item.classId,
                    location: item.locationId,
                    percentage: Number(item.percentage) || 0,
                }));
                setRows(
                    mapped.length ? mapped : [{ id: generateUniqueId(), percentage: 0 }]
                );
            } else {
                setSelectedAccounts([]);
                setSingleAccountOnly(false);
                setRows([{ id: generateUniqueId(), percentage: 0 }]);
            }
        }
    }, [isModalOpen, oneOffConfigurations, generateUniqueId]);

    useEffect(() => {
        const total = rows.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
        const validationErrors: string[] = [];

        if (rows.length && !isPercentageTotalValid(total)) {
            validationErrors.push('Total percentage must equal 100.');
        }

        setValidationError(validationErrors.join('; '));
    }, [rows, selectedAccounts]);

    const addRow = () => {
        setRows((prev) => [...prev, { id: generateUniqueId(), percentage: 0 }]);
        setCsvErrors([]);
    };

    const removeRow = (id: string) => {
        setRows((prev) => prev.filter((r) => r.id !== id));
        setCsvErrors([]);
    };

    const updateRow = (id: string, patch: Partial<AllocationRow>) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
        setCsvErrors([]);
    };

    const handleCsvParsed = (parsedRows: AllocationCsvRow[]) => {
        setRows(
            parsedRows.length
                ? parsedRows.map((row) => ({ id: generateUniqueId(), ...row }))
                : [{ id: generateUniqueId(), percentage: undefined }]
        );
    };

    const beforeUpload = async (file: File) => {
        const maxFileSize = 10 * 1024 * 1024;
        if (file.size > maxFileSize) {
            toastText(
                'File size exceeds 10MB limit. Please select a smaller file.',
                'error'
            );
            return false;
        }

        setUploading(true);
        setImportLoading(true);
        setUploadProgress(0);
        setCsvErrors([]);

        try {
            const text = await file.text();
            const { rows: parsedRows, errors } = await parseAllocationCsv(text, {
                customers: customerIndex,
                classes: classIndex,
                locations: locationIndex,
            });

            // const findValueByTitle = (
            //     title?: string,
            //     list?: Array<{ title: string; value: string }>
            // ) => {
            //     if (!title || !list) return undefined;
            //     const foundOption = list.find(
            //         (option) =>
            //             option.title.toLowerCase().trim() === title.toLowerCase().trim()
            //     );
            //     return foundOption?.value;
            // };

            // const mappedRows = json.map((item) => {
            //     const customerTitle = item['Customer'];
            //     const classTitle = item['Class'];
            //     const locationTitle = item['Location'];
            //     const perc = item['Percentage'];
            //     const customerVal = findValueByTitle(customerTitle, customerIndex);
            //     const classVal = findValueByTitle(classTitle, classIndex);
            //     const locationVal = findValueByTitle(locationTitle, locationIndex);

            //     return {
            //         customer: customerVal,
            //         class: classVal,
            //         location: locationVal,
            //         percentage:
            //             perc !== undefined && perc !== '' ? Number(perc) : undefined,
            //     };
            // });

            setUploadProgress(100);
            if (errors.length > 0) {
                setCsvErrors(errors);
                return false;
            }
            setCsvErrors([]);
            handleCsvParsed(parsedRows);
            toastText('CSV parsed successfully', 'success');
        } catch (e: any) {
            console.error('CSV import failed', e);
            setCsvErrors([e?.message || 'Failed to import CSV']);
            toastText(e?.message || 'Failed to import CSV', 'error');
        } finally {
            setTimeout(() => {
                setUploading(false);
                setImportLoading(false);
            }, 300);
        }
        return false;
    };

    const handleDownloadSample = async () => {
        try {
            setSampleLoading(true);
            const companyId = localStorage.getItem('companyId');
            const res = await getApiCSV(
                '/configure-allocations/allocations/sample-export',
                { companyId }
            );
            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample_allocation.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
            toastText('Sample CSV downloaded', 'success');
        } catch (e: any) {
            console.error('Sample export failed', e);
            toastText('Failed to download sample CSV', 'error');
        } finally {
            setSampleLoading(false);
        }
    };

    const handleProceed = () => {
        if (
            validationError ||
            csvErrors.length > 0 ||
            (singleAccountOnly && selectedAccounts.length === 0)
        )
            return;

        setProceedLoading(true);
        const items = rows
            .map((r) => ({
                percentage: Number(r.percentage) || 0,
                customerId: r.customer || '',
                customerName: findTitleByValue(r.customer, customerIndex) || '',
                classId: r.class || '',
                className: findTitleByValue(r.class, classIndex) || '',
                locationId: r.location || '',
                locationName: findTitleByValue(r.location, locationIndex) || '',
            }))
            .filter((item) => item.percentage > 0); // Filter out entries with percentage = 0

        // Sum up allocations with same customer/class/location combination
        const mergedItems = items.reduce((acc, item) => {
            const key = `${item.customerId || ''}-${item.classId || ''}-${item.locationId || ''}`;
            const existing = acc.find(
                (i) =>
                    `${i.customerId || ''}-${i.classId || ''}-${i.locationId || ''}` === key
            );

            if (existing) {
                // Sum the percentages
                existing.percentage = (existing.percentage || 0) + (item.percentage || 0);
            } else {
                // Add new entry
                acc.push({ ...item });
            }

            return acc;
        }, [] as typeof items);

        // Get accountIds from filters (from transaction tab filters)
        const filterAccountIds = oneOffConfigurations?.accountIds || [];

        // Determine accountIds and configAccount based on switch state
        let accountIdsToSave: string[] = [];
        let configAccount: { name: string; value: string } | null = null;

        if (singleAccountOnly) {
            // Switch is ON:
            // - Use accountIds from filters (transaction tab)
            // - Store single selected account in configAccount
            accountIdsToSave = filterAccountIds;

            if (selectedAccounts.length > 0) {
                const accountId = selectedAccounts[0];
                const accountName = getAccountNameById(accountId);
                if (accountName) {
                    configAccount = {
                        name: accountName,
                        value: accountId,
                    };
                } else {
                    toastText('Unable to find account name for selected account', 'error');
                    setProceedLoading(false);
                    return;
                }
            }
        } else {
            // Switch is OFF:
            // - Use accountIds from filters (transaction tab) - accounts are selected in transaction tab, not in modal
            // - Clear configAccount
            accountIdsToSave = filterAccountIds;
            configAccount = null;
        }

        // Save to Redux only - preserve existing filter values from ExpenseTransactionMain
        const config: IOneOffConfigurations = {
            accountIds: accountIdsToSave,
            // Preserve existing filter values (customerIds, classIds, locationIds, vendorIds) from Redux
            customerIds: oneOffConfigurations?.customerIds || [],
            classIds: oneOffConfigurations?.classIds || [],
            locationIds: oneOffConfigurations?.locationIds || [],
            vendorIds: oneOffConfigurations?.vendorIds || [],
            expenseConfigAllocations: mergedItems.map((item) => ({
                customerId: item.customerId || '',
                customerName: item.customerName || '',
                classId: item.classId || '',
                className: item.className || '',
                locationId: item.locationId || '',
                locationName: item.locationName || '',
                percentage: item.percentage,
            })),
            isSingleAccountAllocation: singleAccountOnly,
            configAccount: configAccount,
        };

        dispatch(updateOneOffConfigurations(config));
        toastText('Configuration saved', 'success');
        onAllocationSaved?.();
        setProceedLoading(false);
        handleCancel();
    };

    const handleReset = () => {
        setSelectedAccounts([]);
        setRows([{ id: generateUniqueId(), percentage: undefined }]);
        setValidationError('');
        setCsvErrors([]);
    };

    const renderAccountSelection = () => {
        // Only show dropdown when switch is ON
        if (!singleAccountOnly) {
            return null;
        }

        return (
            <div className={styles.accountRow}>
                <TreeSelect
                    showSearch
                    allowClear
                    treeDefaultExpandAll
                    filterTreeNode={(inputValue, treeNode) =>
                        (treeNode.props?.title || treeNode.title || '')
                            .toLowerCase()
                            .includes((inputValue || '').toLowerCase())
                    }
                    treeData={treeData.accounts}
                    treeLine
                    placeholder="Select account"
                    style={{ width: '100%' }}
                    className={styles.customTreeSelect}
                    size="large"
                    value={selectedAccounts[0] ?? undefined}
                    onChange={(val) => {
                        const v = (val as string) || undefined;
                        setSelectedAccounts(v ? [v] : []);
                    }}
                />

                {/* Account validation logic moved here */}
                {singleAccountOnly && selectedAccounts.length === 0 && (
                    <div
                        style={{
                            color: '#cf1322',
                            fontSize: 12,
                            marginTop: 10,
                            marginLeft: 10,
                        }}
                    >
                        At least one account is required when single account mode is enabled
                    </div>
                )}
            </div>
        );
    };

    const columns: any[] = [
        {
            title: 'Customer',
            key: 'customer',
            dataIndex: 'customer',
            width: 200,
            render: (_: any, record: AllocationRow) => (
                <TreeSelect
                    showSearch
                    allowClear
                    treeDefaultExpandAll
                    filterTreeNode={(inputValue, treeNode) =>
                        (treeNode.props?.title || treeNode.title || '')
                            .toLowerCase()
                            .includes((inputValue || '').toLowerCase())
                    }
                    value={record.customer}
                    treeData={treeData.customers}
                    placeholder={'Select Customer'}
                    onChange={(val) => updateRow(record.id, { customer: val as string })}
                    style={{ width: '100%' }}
                    size="large"
                    treeLine
                    className={styles.customTreeSelect}
                />
            ),
        },
        {
            title: 'Class',
            key: 'class',
            dataIndex: 'class',
            width: 180,
            render: (_: any, record: AllocationRow) => (
                <TreeSelect
                    showSearch
                    allowClear
                    treeDefaultExpandAll
                    filterTreeNode={(inputValue, treeNode) =>
                        (treeNode.props?.title || treeNode.title || '')
                            .toLowerCase()
                            .includes((inputValue || '').toLowerCase())
                    }
                    value={record.class}
                    treeData={treeData.classes}
                    placeholder={'Select Class'}
                    onChange={(val) => updateRow(record.id, { class: val as string })}
                    style={{ width: '100%' }}
                    size="large"
                    treeLine
                    className={styles.customTreeSelect}
                />
            ),
        },
        {
            title: 'Location',
            key: 'location',
            dataIndex: 'location',
            width: 180,
            render: (_: any, record: AllocationRow) => (
                <TreeSelect
                    showSearch
                    allowClear
                    treeDefaultExpandAll
                    filterTreeNode={(inputValue, treeNode) =>
                        (treeNode.props?.title || treeNode.title || '')
                            .toLowerCase()
                            .includes((inputValue || '').toLowerCase())
                    }
                    value={record.location}
                    treeData={treeData.locations}
                    placeholder={'Select Location'}
                    onChange={(val) => updateRow(record.id, { location: val as string })}
                    style={{ width: '100%' }}
                    size="large"
                    treeLine
                    className={styles.customTreeSelect}
                />
            ),
        },
        {
            title: 'Percentage',
            key: 'percentage',
            dataIndex: 'percentage',
            width: 120,
            render: (_: any, record: AllocationRow) => (
                <InputNumber
                    min={0}
                    max={100}
                    addonAfter="%"
                    value={record.percentage}
                    onChange={(val) =>
                        updateRow(record.id, { percentage: (val as number) ?? undefined })
                    }
                    size="large"
                    style={{ width: '100%' }}
                    className={styles.percentInput}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            align: 'center' as const,
            render: (_: any, record: AllocationRow) => (
                <Space size={8}>
                    {rows.length > 1 && (
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeRow(record.id)}
                            className={styles.actionButton}
                            size="small"
                        ></Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                className="custom-modal"
                closable={true}
                footer={false}
                width={1200}
                maskClosable={false}
            >
                <Spin
                    spinning={importLoading || proceedLoading}
                    indicator={
                        <LoadingOutlined style={{ fontSize: 24, color: '#344735' }} spin />
                    }
                    size="large"
                >
                    <Row className={styles['modal__header']}>
                        <h4 className={styles['modal__title']}>
                            Configure Allocations
                        </h4>
                    </Row>

                    <div className={styles['modal__body']}>
                        <div className={styles.section}>
                            <div
                                className={styles.sectionHeader}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    gap: 24,
                                }}
                            >
                                <span>Account Selection</span>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '12px',
                                            color: '#344735',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Single Account
                                    </span>
                                    <Switch
                                        checked={singleAccountOnly}
                                        onChange={(checked) => {
                                            setSingleAccountOnly(checked);
                                            if (checked) {
                                                // When switching ON: only set account if configAccount exists from backend
                                                if (oneOffConfigurations?.configAccount) {
                                                    setSelectedAccounts([oneOffConfigurations.configAccount.value]);
                                                } else {
                                                    // No configAccount from backend, so no account selected
                                                    setSelectedAccounts([]);
                                                }
                                            } else {
                                                // When switching OFF, clear selected accounts since we'll use accountIds from filters
                                                setSelectedAccounts([]);
                                            }
                                        }}
                                    />
                                    <Tooltip
                                        title={
                                            <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                                <div style={{ marginBottom: '8px' }}>
                                                    <strong>When turned off:</strong> Allocations debit and credit the original expense accounts in your filtered transactions.
                                                </div>
                                                <div>
                                                    <strong>When turned on:</strong> All filtered transactions are summed and posted to the single account you select here. Debits follow the allocation breakdown you define, while credits return to the same single account using the original customers, classes, and locations.
                                                </div>
                                            </div>
                                        }
                                        placement="right"
                                        overlayStyle={{ zIndex: 10000 }}
                                        overlayInnerStyle={{ width: '520px',maxWidth: '600px', padding: '12px' }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                fontSize: '20px',
                                                color: '#999',
                                                cursor: 'help',
                                                marginLeft: '2px',
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            </div>
                            {renderAccountSelection()}
                        </div>

                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                Allocation Breakdown
                            </div>
                            <div className={styles.tableHeaderActions}>
                                <Space size={8}>
                                    <Button
                                        size="small"
                                        icon={<UploadOutlined />}
                                        loading={sampleLoading}
                                        onClick={handleDownloadSample}
                                        className={styles.headerButtons}
                                        style={{
                                            backgroundColor: '#344735',
                                            fontSize: '12px',
                                            borderRadius: '12px',
                                            height: '41px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            width: '13rem',
                                        }}
                                    >
                                        Sample CSV
                                    </Button>
                                    <Upload
                                        accept={'.csv'}
                                        beforeUpload={beforeUpload}
                                        showUploadList={false}
                                        disabled={importLoading}
                                    >
                                        <Button
                                            size="small"
                                            icon={<DownloadOutlined />}
                                            loading={importLoading}
                                            disabled={importLoading}
                                            className={styles['headerButtons']}
                                            style={{
                                                backgroundColor: importLoading ? '#ccc' : '#344735',
                                                fontSize: '12px',
                                                borderRadius: '12px',
                                                height: '41px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                width: '13rem',
                                            }}
                                        >
                                            {importLoading ? 'Importing...' : 'Import'}
                                        </Button>
                                    </Upload>
                                </Space>
                            </div>
                            {uploading && (
                                <div style={{ marginBottom: 8 }}>
                                    <Progress
                                        percent={uploadProgress}
                                        size="small"
                                        status={uploadProgress === 100 ? 'success' : 'active'}
                                        format={(percent) => {
                                            if (percent === 0) return 'Parsing CSV...';
                                            if (percent === 100) return 'Import complete!';
                                            return `${percent}%`;
                                        }}
                                    />
                                </div>
                            )}
                            <Table
                                columns={columns}
                                dataSource={rows}
                                rowKey="id"
                                pagination={false}
                                className="table-global"
                                size="small"
                                scroll={{ y: 240, x: true as any }}
                            />
                            <div onClick={addRow} className={styles['add-button']}>
                                <BlueAddSvg />
                                <p className={styles['add-more']}>Add Another Item</p>
                            </div>
                            <div
                                style={{
                                    marginTop: 12,
                                    padding: '8px 12px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 4,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    textAlign: 'right',
                                }}
                            >
                               Total Percentage:{' '}
                                {(() => {
                                    const total = rows.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
                                    // Round to 2 decimal places, and if very close to 100, show 100
                                    const rounded = Math.round(total * 100) / 100;
                                    return Math.abs(rounded - 100) < 0.01 ? 100 : rounded.toFixed(2);
                                })()}%
                            </div>
                            {validationError && csvErrors.length === 0 && (
                                <div style={{ color: '#cf1322', fontSize: 12, marginTop: 6 }}>
                                    {validationError}
                                </div>
                            )}
                            {csvErrors.length > 0 && (
                                <div style={{ color: '#cf1322', fontSize: 12, marginTop: 6 }}>
                                    {csvErrors.map((e, i) => (
                                        <div key={i}>{e}</div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className={styles['modal__footer']}>
                            <div>
                                <Button className={styles['reset']} onClick={handleReset}>
                                    Reset
                                </Button>
                            </div>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <Button className={styles['cancel']} onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    className={`${styles['save']} ${proceedLoading && 'pointer-event-none'
                                        }`}
                                    onClick={handleProceed}
                                    disabled={
                                        !!validationError ||
                                        csvErrors.length > 0 ||
                                        proceedLoading ||
                                        (singleAccountOnly && selectedAccounts.length === 0)
                                    }
                                >
                                    {proceedLoading ? (
                                        <img src="/assets/gifs/loading-black.gif" height={40} />
                                    ) : (
                                        'Proceed with Allocation'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Spin>
            </Modal>
        </>
    );
};

export default OneOffSettingsModal;

